/** Browser lifecycle contract for ambiguous campaign automation operations. */

/** Maximum time spent reconciling before requesting durable cancellation. */
export const AUTOMATION_OPERATION_TIMEOUT_MS = 30_000;
/** Delay between receipt reads while an operation remains non-terminal. */
export const AUTOMATION_OPERATION_POLL_MS = 750;

/** Enable identity retained until completion or confirmed cancellation. */
export interface PendingAutomationEnableOperation {
  /** Caller-owned operation UUID. */
  operationId: string;
  /** Desired state protected from stale flow reads. */
  desiredEnabled: true;
  /** Start of the bounded reconciliation budget, in epoch milliseconds. */
  startedAt: number;
}

/** Participant preview identity retained across review and confirmation. */
export interface PendingParticipantImportOperation {
  /** Server-owned preview UUID. */
  previewId: string;
  /** Confirmation start, or null while the frozen preview awaits approval. */
  confirmationStartedAt: number | null;
}

/** Build the tab-scoped enable operation key for one campaign. */
export function automationEnableOperationStorageKey(campaignId: string): string {
  return `holito.automation-enable-operation.v1.${campaignId}`;
}

/** Build the tab-scoped participant import key for one campaign. */
export function participantImportOperationStorageKey(campaignId: string): string {
  return `holito.participant-import-operation.v1.${campaignId}`;
}

/** Read one JSON value without making unavailable browser storage fatal. */
function readStoredValue(key: string): Record<string, unknown> | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw) as unknown;
    return typeof value === 'object' && value !== null && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
}

/** Persist one operation before its mutation request starts. */
function writeStoredValue(key: string, value: object): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The caller keeps the same identity in memory when storage is unavailable.
  }
}

/** Clear only after a terminal result is known. */
function clearStoredValue(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Restricted browser contexts can make session storage unavailable.
  }
}

/** Restore an enable operation after navigation or refresh. */
export function readPendingAutomationEnable(
  campaignId: string,
): PendingAutomationEnableOperation | null {
  const value = readStoredValue(automationEnableOperationStorageKey(campaignId));
  if (
    !value
    || typeof value.operationId !== 'string'
    || value.desiredEnabled !== true
    || typeof value.startedAt !== 'number'
    || !Number.isFinite(value.startedAt)
  ) return null;
  return {
    operationId: value.operationId,
    desiredEnabled: true,
    startedAt: value.startedAt,
  };
}

/** Persist an enable identity before dispatching the request. */
export function writePendingAutomationEnable(
  campaignId: string,
  operation: PendingAutomationEnableOperation,
): void {
  writeStoredValue(automationEnableOperationStorageKey(campaignId), operation);
}

/** Clear a terminal enable identity. */
export function clearPendingAutomationEnable(campaignId: string): void {
  clearStoredValue(automationEnableOperationStorageKey(campaignId));
}

/** Restore a participant preview or confirmation after navigation or refresh. */
export function readPendingParticipantImport(
  campaignId: string,
): PendingParticipantImportOperation | null {
  const value = readStoredValue(participantImportOperationStorageKey(campaignId));
  const startedAt = value?.confirmationStartedAt;
  if (
    !value
    || typeof value.previewId !== 'string'
    || !(
      startedAt === null
      || (typeof startedAt === 'number' && Number.isFinite(startedAt))
    )
  ) return null;
  return { previewId: value.previewId, confirmationStartedAt: startedAt };
}

/** Persist a participant preview or confirmation identity. */
export function writePendingParticipantImport(
  campaignId: string,
  operation: PendingParticipantImportOperation,
): void {
  writeStoredValue(participantImportOperationStorageKey(campaignId), operation);
}

/** Clear a terminal participant import identity. */
export function clearPendingParticipantImport(campaignId: string): void {
  clearStoredValue(participantImportOperationStorageKey(campaignId));
}
